import { Bell, Download, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  browserNotificationPreference,
  shouldOfferBrowserNotifications,
} from '@/lib/browserNotificationPreference';
import {
  ensureWebPushSubscription,
  synchroniseWebPushPreference,
  type WebPushStatus,
  wasWebPushEnabled,
  warmWebPushPrerequisites,
} from '@/lib/webPush';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const INSTALLED_MARKER = 'carrfields-pwa-installed';
const NOTIFICATION_DISMISS_PREFIX = 'carrfields-notification-dismissed-until:';
const NOTIFICATION_DISMISS_DAYS = 7;
type NotificationBannerStatus = WebPushStatus | 'checking' | 'connecting' | 'error';

function isStandalone(): boolean {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || standaloneNavigator.standalone === true;
}

function hasInstalledMarker(): boolean {
  try {
    return localStorage.getItem(INSTALLED_MARKER) === 'true';
  } catch {
    return false;
  }
}

function setInstalledMarker(installed: boolean): void {
  try {
    if (installed) localStorage.setItem(INSTALLED_MARKER, 'true');
    else localStorage.removeItem(INSTALLED_MARKER);
  } catch {
    // Installation still works when storage is unavailable.
  }
}

function isAppleMobileDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isMobileDevice(): boolean {
  return isAppleMobileDevice() || /Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);
}

function isNotificationBannerDismissed(userId: string): boolean {
  try {
    const raw = localStorage.getItem(`${NOTIFICATION_DISMISS_PREFIX}${userId}`);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    if (Date.now() >= until) {
      localStorage.removeItem(`${NOTIFICATION_DISMISS_PREFIX}${userId}`);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function setNotificationBannerDismissed(userId: string): void {
  try {
    const until = Date.now() + NOTIFICATION_DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(`${NOTIFICATION_DISMISS_PREFIX}${userId}`, String(until));
  } catch {
    // Dismissal still works for the current page view when storage is unavailable.
  }
}

export function PwaControls() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { pathname } = useLocation();
  // On iOS, an invitee opening the registration link gets this install
  // banner overlapping the signup form before they've even finished
  // creating their account — it acts as a barrier to completing
  // registration. Hold off showing it on /signup; it picks back up as soon
  // as they land on /login (or anywhere else) right after they've chosen
  // their password and submitted the form.
  const onSignupPage = pathname === '/signup';
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [notificationDismissed, setNotificationDismissed] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<NotificationBannerStatus>('checking');
  const [notificationPending, setNotificationPending] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone() || hasInstalledMarker());
  const appleMobile = isAppleMobileDevice();
  const mobileDevice = isMobileDevice();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)');
    const markInstalled = () => {
      setInstalledMarker(true);
      setInstalled(true);
      setInstallPrompt(null);
    };
    const onDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches) markInstalled();
    };
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstalledMarker(false);
      setInstalled(false);
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', markInstalled);
    displayMode.addEventListener('change', onDisplayModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', markInstalled);
      displayMode.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  useEffect(() => {
    if (needRefresh && import.meta.env.PROD) void updateServiceWorker(true);
  }, [needRefresh, updateServiceWorker]);

  useEffect(() => {
    let cancelled = false;
    setNotificationDismissed(user?.userId ? isNotificationBannerDismissed(user.userId) : false);
    setNotificationStatus('checking');
    if (!user?.userId) {
      return;
    }

    warmWebPushPrerequisites();
    if (
      'Notification' in window &&
      Notification.permission === 'granted' &&
      wasWebPushEnabled(user.userId)
    ) {
      setNotificationStatus('connecting');
    }
    void (async () => {
      try {
        const syncedStatus = await synchroniseWebPushPreference(user.userId);
        if (!cancelled) setNotificationStatus(syncedStatus);
      } catch (error) {
        if (!cancelled) setNotificationStatus('error');
        console.warn('Unable to synchronise browser notifications.', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.userId]);

  const install = async () => {
    if (!installPrompt) return;

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === 'accepted') {
      setInstalledMarker(true);
      setInstalled(true);
    }
  };

  // Dismissal is only remembered for the current page view; a refresh (or
  // revisiting the home page) will show the install banner again.
  const dismissInstall = () => {
    setInstallDismissed(true);
  };

  const enableNotifications = async () => {
    if (!user?.userId || notificationPending) return;
    setNotificationPending(true);
    let permissionAcknowledged = false;
    try {
      const status = await ensureWebPushSubscription(user.userId, true, () => {
        permissionAcknowledged = true;
        setNotificationStatus('connecting');
        setNotificationPending(false);
        setNotificationDismissed(false);
        showToast('Browser notifications enabled.', 'success');
      });
      setNotificationStatus(status);
      if (status === 'subscribed') {
        setNotificationDismissed(false);
        if (!permissionAcknowledged) {
          showToast('Browser notifications enabled.', 'success');
        }
      } else if (status === 'denied') {
        showToast('Notifications are blocked in this browser. Allow them in the site settings and try again.', 'error');
      } else if (status === 'unconfigured') {
        showToast('Browser notifications are not configured on the server.', 'error');
      }
    } catch (error) {
      setNotificationStatus('error');
      showToast(
        permissionAcknowledged
          ? 'Notifications are allowed, but this device could not finish connecting. Tap Enable notifications to retry.'
          : error instanceof Error
            ? error.message
            : 'Unable to enable browser notifications.',
        'error',
      );
    } finally {
      setNotificationPending(false);
    }
  };

  const dismissNotifications = () => {
    setNotificationDismissed(true);
    if (user?.userId) setNotificationBannerDismissed(user.userId);
  };

  const showNotificationBanner =
    mobileDevice &&
    Boolean(user?.userId) &&
    shouldOfferBrowserNotifications(
      browserNotificationPreference(user!.userId),
      notificationStatus,
      notificationDismissed,
    );
  const showInstallBanner =
    !onSignupPage &&
    !showNotificationBanner &&
    !installed &&
    !installDismissed &&
    !(Boolean(user?.userId) && !mobileDevice) &&
    (appleMobile || Boolean(installPrompt));
  if (!showInstallBanner && !showNotificationBanner) return null;

  return (
    <div className="pwa-controls" id="install-app">
      {showInstallBanner && (
        <aside className="pwa-install-banner" aria-label="Install Carrfields">
          <span className="pwa-install-banner__icon" aria-hidden="true"><Smartphone size={24} /></span>
          <div className="pwa-install-banner__copy">
            <strong>Install Carrfields</strong>
            <p>
              {appleMobile
                ? 'Tap the Share button in your browser, then choose Add to Home Screen.'
                : 'Add Carrfields to your device for faster access and a full-screen app experience.'}
            </p>
          </div>
          <div className="pwa-install-banner__actions">
            {!appleMobile && (
              <Button size="sm" disabled={!installPrompt} aria-busy={!installPrompt} onClick={() => void install()}>
                <Download size={17} /> Install app
              </Button>
            )}
            <button type="button" className="pwa-install-banner__dismiss" onClick={dismissInstall}>
              Dismiss
            </button>
          </div>
        </aside>
      )}

      {showNotificationBanner && (
        <aside
          className="pwa-install-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Enable Carrfields notifications"
        >
          <span className="pwa-install-banner__icon" aria-hidden="true"><Bell size={24} /></span>
          <div className="pwa-install-banner__copy">
            <strong>Enable notifications</strong>
            <p>
              {notificationStatus === 'denied'
                ? 'Notifications are blocked. Allow them for Carrfields in your browser or Android site settings, then try again.'
                : notificationStatus === 'error'
                  ? 'Carrfields could not connect this device to notification delivery. Try again to reconnect it.'
                  : 'Get Carrfields updates on this device, even when the app is closed.'}
            </p>
          </div>
          <div className="pwa-install-banner__actions">
            <Button size="sm" disabled={notificationPending} onClick={() => void enableNotifications()}>
              <Bell size={17} /> {notificationPending ? 'Enabling…' : 'Enable notifications'}
            </Button>
            <button type="button" className="pwa-install-banner__dismiss" onClick={dismissNotifications}>
              Not now
            </button>
          </div>
        </aside>
      )}
    </div>
  );
}
